import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { API_URL } from "../api";
import { getOptimizedCloudinaryUrl } from "../utils/cloudinary";
import {
  MIN_DRIVER_AGE,
  formatAed,
  formatBookingDateTime,
  getLatestDateOfBirth,
  getPriceLines,
  getRentalDaysLabel,
} from "../utils/booking";
import { getCarDisplayName } from "./CarsList";

export const PENDING_PAYMENT_KEY = "pendingPaymentRef";

const initialCustomer = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: "",
};

function Booking() {
  const { carId } = useParams();
  const [searchParams] = useSearchParams();
  const period = searchParams.get("period");
  const pickupAt = searchParams.get("pickup");
  const returnAt = searchParams.get("return");

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [isLoadingQuote, setIsLoadingQuote] = useState(true);
  const [customer, setCustomer] = useState(initialCustomer);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoadingQuote(true);
    setQuoteError("");

    fetch(`${API_URL}/payments/quote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ carId, period, pickupAt, returnAt }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Could not load your booking.");
        }
        setQuote(data);
      })
      .catch((error) => {
        setQuote(null);
        setQuoteError(error.message || "Could not load your booking.");
      })
      .finally(() => setIsLoadingQuote(false));
  }, [carId, period, pickupAt, returnAt]);

  const handleCustomerChange = (event) => {
    const { name, value } = event.target;
    setCustomer((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    if (!termsAccepted) {
      setSubmitError("Please accept the Terms & Conditions.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          carId,
          period,
          pickupAt,
          returnAt,
          ...customer,
          termsAccepted,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Could not start the payment.");
      }

      try {
        sessionStorage.setItem(PENDING_PAYMENT_KEY, data.orderRef);
      } catch (storageError) {
        // The payment page returns the order reference in the URL anyway.
      }
      window.location.assign(data.paymentUrl);
    } catch (error) {
      setSubmitError(error.message || "Could not start the payment.");
      setIsSubmitting(false);
    }
  };

  if (isLoadingQuote) {
    return (
      <main className="bookingPage">
        <p className="bookingLoading">Loading your booking...</p>
      </main>
    );
  }

  if (quoteError || !quote) {
    return (
      <main className="bookingPage">
        <section className="bookingNotice">
          <h1>We could not prepare this booking</h1>
          <p>{quoteError}</p>
          <Link to="/" className="bookingBackLink">
            Back to cars
          </Link>
        </section>
      </main>
    );
  }

  const { car, pricing } = quote;
  const carName = getCarDisplayName(car);

  return (
    <main className="bookingPage">
      <header className="bookingHero">
        <p>Secure checkout</p>
        <h1>Review your booking</h1>
      </header>

      <div className="bookingLayout">
        <section className="bookingCard bookingSummary">
          <img
            src={getOptimizedCloudinaryUrl(car.imageUrl, 640, 420)}
            alt={carName}
            width="640"
            height="420"
          />
          <h2>
            {carName}
            <small>or similar</small>
          </h2>
          <p className="bookingCapacity">{car.capacity} seats</p>

          <dl className="bookingDetails">
            <div>
              <dt>Rent option</dt>
              <dd>{pricing.period === "monthly" ? "Monthly" : "Daily"}</dd>
            </div>
            <div>
              <dt>Pickup</dt>
              <dd>{formatBookingDateTime(pickupAt)}</dd>
            </div>
            <div>
              <dt>Return</dt>
              <dd>{formatBookingDateTime(returnAt)}</dd>
            </div>
            <div>
              <dt>Rental period</dt>
              <dd>{getRentalDaysLabel(pricing)}</dd>
            </div>
          </dl>

          <dl className="bookingPrice">
            {getPriceLines(pricing).map((line) => (
              <div key={line.label}>
                <dt>{line.label}</dt>
                <dd>{formatAed(line.value)}</dd>
              </div>
            ))}
            <div>
              <dt>Subtotal</dt>
              <dd>{formatAed(pricing.subtotal)}</dd>
            </div>
            <div>
              <dt>VAT ({pricing.vatPercent}%)</dt>
              <dd>{formatAed(pricing.vat)}</dd>
            </div>
            <div className="bookingTotal">
              <dt>Total</dt>
              <dd>{formatAed(pricing.total)}</dd>
            </div>
          </dl>

          <ul className="bookingNotes">
            <li>Comprehensive insurance included.</li>
            <li>
              Salik (tolls), traffic fines and similar charges are billed
              separately after the rental.
            </li>
            <li>
              Each rental day is counted as 24 hours from pickup time. Returns
              up to 2 hours late are free; beyond that, an extra day is
              charged.
            </li>
            <li>The deposit amount will be refunded to the original payment method within 24 working days.</li>
          </ul>
        </section>

        <form className="bookingCard bookingForm" onSubmit={handleSubmit}>
          <h2>Driver details</h2>
          <div className="bookingFormGrid">
            <label>
              First name
              <input
                type="text"
                name="firstName"
                autoComplete="given-name"
                maxLength={60}
                value={customer.firstName}
                onChange={handleCustomerChange}
                required
              />
            </label>
            <label>
              Last name
              <input
                type="text"
                name="lastName"
                autoComplete="family-name"
                maxLength={60}
                value={customer.lastName}
                onChange={handleCustomerChange}
                required
              />
            </label>
            <label>
              Email
              <input
                type="email"
                name="email"
                autoComplete="email"
                maxLength={120}
                value={customer.email}
                onChange={handleCustomerChange}
                required
              />
            </label>
            <label>
              Phone number
              <input
                type="tel"
                name="phone"
                autoComplete="tel"
                placeholder="+971 50 123 4567"
                value={customer.phone}
                onChange={handleCustomerChange}
                required
              />
            </label>
            <label className="bookingFormWide">
              Date of birth
              <input
                type="date"
                name="dateOfBirth"
                autoComplete="bday"
                max={getLatestDateOfBirth()}
                value={customer.dateOfBirth}
                onChange={handleCustomerChange}
                required
              />
              <span className="bookingHint">
                The driver must be at least {MIN_DRIVER_AGE} years old.
              </span>
            </label>
          </div>

          <label className="bookingTerms">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
            />
            <span>
              I have read and agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                Terms & Conditions
              </a>
              .
            </span>
          </label>

          {submitError && <p className="bookingError">{submitError}</p>}

          <button
            type="submit"
            className="bookingPayButton"
            disabled={isSubmitting || !termsAccepted}
          >
            {isSubmitting
              ? "Redirecting to payment..."
              : `Pay ${formatAed(pricing.total)}`}
          </button>
          <p className="bookingSecureNote">
            You will be redirected to the secure Network International payment
            page to enter your card details.
          </p>
        </form>
      </div>
    </main>
  );
}

export default Booking;
